import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { vorgangService, type TodoDto } from "../../../services/vorgangService";
import type { AppUser } from "../../../services/userService";
import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import { slotsFromDto, type CascadeResult } from "../detail/linkSlots";
import type { BeteiligtenFeld } from "../detail/personenZeile";
import { canRemoveTodo, istTodoErledigt, toggledTodoStatus } from "../detail/todoRowActions";
import { BeteiligteFeld } from "../v3/BeteiligteFeld";
import { BezugsKreise } from "../v3/BezugsKreise";
import { IcoCheck, IcoChevronUnten, IcoMehrWaagerecht, IcoPlus } from "../v3/vg3Icons";
import type { VerlaufEintrag } from "../v3/verlauf";
import { ZusammenfassungDialog } from "../v3/ZusammenfassungDialog";
import { RdPopover } from "../../redesign/table/RdPopover";
import { ArbeitsstrangV4, type StrangHandlers } from "./ArbeitsstrangV4";
import type { VorgangEntwurf } from "../../../services/graphMailService";
import { DatumChipV4 } from "./DatumChipV4";
import { DateiDropZoneV4 } from "./DateiDropZoneV4";
import { AustauschKnopfV4 } from "./AustauschKnopfV4";
import type { AustauschDto } from "../../../services/vorgangAustauschService";
import { TodoZusammenfuehrenDialog } from "./TodoZusammenfuehrenDialog";
import { TodoHinausDialog } from "./TodoHinausDialog";
import { darfAufTodo, istTodoZug, ZIEHTYP_TODO, type TodoZug } from "../shared/todoZug";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { bestaetigen, hinweisen } from "../../redesign/bestaetigen";
import { twvPatch } from "../shared/fristZurTwv";
import { fristPille } from "./mobil/fristPille";
import { FristSheet } from "./mobil/FristSheet";
import { entfernenText } from "./todoRueckfaltung";

/**
 * Ein To-do in V4 — derselbe Kopf wie der Vorgang, derselbe Notizstrom
 * darunter.
 *
 *     ∨  To-do-Betreff        (K)(R)(V) A ⋯ [als erledigt markieren]
 *     TWV [Fr., 18.09.2026]  FRIST [—]   (○)      (○○)      (○)
 *                                  Hauptver.  Bearbeiter  zu informieren
 *
 * Der graue Zähler zwischen ⊕ und ⋯ ist am 13.09.2026 entfallen; das ⊕
 * selbst am 29.10.2026 — an seiner Stelle steht das A des Austauschs (T-302).
 *
 * Dass Vorgang und To-do gleich aussehen, ist der Punkt: es ist derselbe
 * Gegenstand in klein. Wer den Vorgang lesen kann, kann auch das To-do lesen.
 *
 * Aufgeklappt trägt es seinen eigenen Notizbereich samt Ereignissen und die
 * eigene Aktionsleiste — eine To-do-Detailseite gibt es nicht, die Ausklappung
 * IST die Detailansicht.
 */
type Props = StrangHandlers & {
    todo: TodoDto;
    eintraege: VerlaufEintrag[];
    alleNotizen: AggregatedNoteDto[];
    users: AppUser[];
    canEdit: boolean;
    vorgangId: number;
    contactId?: number | null;
    contactName?: string | null;
    vorgangNbr?: string | null;
    vertragNr?: string | null;
    risikoName?: string | null;
    zugewieseneMessageIds: Set<string>;
    /** Schon im Strom liegende Dokumente — die Auswahl graut sie aus. */
    vorhandeneDokumentIds?: ReadonlySet<number>;
    risiken?: Array<{ id: number; label: string }>;
    vertraege?: Array<{ id: number; label: string }>;
    aktuellerUserId: string;
    /**
     * Die ÜBRIGEN To-dos dieses Vorgangs, je mit ihrer Zählung („2 Notizen ·
     * 5 im Verlauf") — Grundlage für „To-do zusammenführen…" im ⋯-Menü
     * (PO 16.09.2026). Leer oder nicht gesetzt: der Menüpunkt entfällt.
     *
     * Die Zählung kommt FERTIG von der Seite, nicht aus der Karte: sie
     * bräuchte dafür den Verlauf aller anderen To-dos, den die Seite ohnehin
     * schon hat (`strangZaehlung.ts`). Die Karte kennt nur ihren eigenen.
     */
    andereTodos?: Array<{ todo: TodoDto; zaehlung: string }>;
    /** Mail-Entwürfe an diesem To-do — als Karten in seinem Verlauf; `null` = lädt noch. */
    entwuerfe?: ReadonlyArray<VorgangEntwurf> | null;
    /**
     * Die Austausche des Vorgangs (T-302) — das A zeigt den dieses To-dos.
     * Fehlt die Liste (lädt noch), steht das A grau da.
     */
    austausch?: readonly AustauschDto[];
    onAustauschGeaendert?: () => void | Promise<void>;
    /**
     * Sprungmarke (`?todo=`, User 01.11.2026): jeder neue Wert > 0 klappt die
     * Karte auf. Ein Zähler statt eines Schalters, damit ein zweiter Sprung
     * auf dieselbe, inzwischen wieder zugeklappte Karte auch wirkt.
     */
    aufklappen?: number;
    /**
     * KONTROLLIERT (Plan „Vorgangsdetail mobil", PR 1): Die Seite hält, welche
     * To-dos offen sind — das Plus auf dem Handy schlägt das offene als Ziel
     * vor, und dort ist immer nur eines offen (Akkordeon). Fehlt `offen`,
     * führt die Karte ihren Zustand wie bisher selbst.
     */
    offen?: boolean;
    onOffen?: (offen: boolean) => void;
    /**
     * HANDY-LAYOUT (Plan „Vorgangsdetail mobil", PR 2): kompakte Zeile mit
     * Haken (44 px), Titel, Frist-Pille, Zähler und Chevron; aufgeklappt
     * Werkzeuge, Fristen/Rollen und der Strang. Kein Ziehen, keine Dateiablage
     * — dafür gibt es Menüwege.
     */
    mobil?: boolean;
    /** „2 Notizen · 1 im Verlauf" für die kompakte Zeile (`strangZaehlung`). */
    zaehlung?: string;
    /**
     * „+ Hinzufügen" am aufgeklappten To-do (nur `mobil`): öffnet das Plus mit
     * diesem To-do als festem Ziel — statt der Aktionsleiste.
     */
    onHinzufuegen?: () => void;
};

/**
 * Das gerade gezogene To-do (PO 28.09.2026: „zusammenführen der To-dos über
 * Drag and Drop").
 *
 * Modulweit statt im State: Quelle und Ziel sind VERSCHIEDENE Karten, und
 * beim Überfahren gibt der Browser die Nutzlast nicht heraus — nur die Typen.
 * Ohne diesen Merker leuchtete jede Karte auf, auch die gezogene selbst und
 * die To-dos, die das Backend gar nicht annimmt. Kein `setState` beim
 * `dragstart`: Chrome bricht den Zug ab, wenn mitten darin neu gezeichnet wird.
 */
let gezogeneKarte: { id: number; vorgangId: number } | null = null;

export const TodoKarteV4: React.FC<Props> = ({
    todo,
    eintraege,
    alleNotizen,
    users,
    canEdit,
    vorgangId,
    contactId,
    contactName,
    vorgangNbr,
    vertragNr,
    risikoName,
    zugewieseneMessageIds,
    vorhandeneDokumentIds,
    risiken,
    vertraege,
    aktuellerUserId,
    andereTodos,
    entwuerfe,
    austausch,
    onAustauschGeaendert,
    aufklappen,
    offen: offenVonAussen,
    onOffen,
    mobil = false,
    zaehlung,
    onHinzufuegen,
    ...handlers
}) => {
    const [offenSelbst, setOffenSelbst] = useState(() => (aufklappen ?? 0) > 0);
    useEffect(() => {
        if ((aufklappen ?? 0) > 0) setOffenSelbst(true);
    }, [aufklappen]);
    const kontrolliert = offenVonAussen !== undefined;
    const offen = kontrolliert ? offenVonAussen : offenSelbst;
    const setOffen = (wert: boolean | ((vorher: boolean) => boolean)) => {
        const neu = typeof wert === "function" ? wert(offen) : wert;
        if (!kontrolliert) setOffenSelbst(neu);
        if (neu !== offen) onOffen?.(neu);
    };
    const [zusammenfuehren, setZusammenfuehren] = useState(false);
    /** „To-do verschieben…" — wohin? (PO 22.09.2026) */
    const [hinaus, setHinaus] = useState(false);
    const navigate = useNavigate();
    const kandidaten = useMemo(
        () => (andereTodos ?? []).filter((k) => k.todo.id !== todo.id),
        [andereTodos, todo.id],
    );
    /** Leuchtet auf, solange ein Geschwister-To-do über dieser Karte schwebt. */
    const [treffer, setTreffer] = useState(false);
    const trefferTiefe = React.useRef(0);
    /** Das per Zug hereingefallene To-do — öffnet den Zusammenführen-Dialog. */
    const [zugQuelleId, setZugQuelleId] = useState<number | null>(null);
    const zugQuelle = useMemo(
        () => (andereTodos ?? []).find((k) => k.todo.id === zugQuelleId)?.todo ?? null,
        [andereTodos, zugQuelleId],
    );
    const zugKandidaten = useMemo(
        () => (andereTodos ?? []).filter((k) => k.todo.id !== zugQuelleId),
        [andereTodos, zugQuelleId],
    );
    const nimmtZugAn = (e: React.DragEvent) =>
        canEdit &&
        istTodoZug(e.dataTransfer.types) &&
        darfAufTodo(gezogeneKarte, { id: todo.id, vorgangId });
    const [titel, setTitel] = useState(todo.titel);
    const [umbenennen, setUmbenennen] = useState(false);
    const [abschlussFrage, setAbschlussFrage] = useState(false);
    /** Handy: Fristen-Sheet hinter der Frist-Pille (Plan mobil, PR 7). */
    const [fristSheet, setFristSheet] = useState(false);
    const done = istTodoErledigt(todo.status);
    const werte = {
        hauptverantwortlich: todo.hauptverantwortlich ?? [],
        bearbeiter: (todo.bearbeiter ?? []).map(String),
        zuInformierende: todo.zuInformierende ?? [],
    };

    // Ein Titel, den jemand anderes geändert hat, muss auch im geschlossenen
    // Feld ankommen — der lokale Entwurf folgt dem geladenen Wert.
    useEffect(() => setTitel(todo.titel), [todo.titel]);

    const patch = async (data: Partial<TodoDto>) => {
        try {
            await vorgangService.updateTodo(todo.id, data);
            await handlers.onGeaendert();
        } catch (err: any) {
            console.error("[TodoKarteV4] Speichern fehlgeschlagen", err);
            void hinweisen({ titel: "Nicht gespeichert", text: err?.message ?? "Speichern fehlgeschlagen.", ton: "danger" });
        }
    };

    const patchBeteiligte = async (feld: BeteiligtenFeld, next: string[]) => {
        try {
            // Bearbeiter/Hauptverantwortliche laufen über den eigenen,
            // autorisierten Endpoint; der generische PUT ignoriert sie.
            if (feld === "bearbeiter" || feld === "hauptverantwortlich") {
                await vorgangService.updateTodoBeteiligte(todo.id, { [feld]: next });
            } else {
                await vorgangService.updateTodo(todo.id, { [feld]: next } as Partial<TodoDto>);
            }
            await handlers.onGeaendert();
        } catch (err: any) {
            console.error("[TodoKarteV4] Beteiligte speichern fehlgeschlagen", err);
            void hinweisen({ titel: "Nicht gespeichert", text: err?.message ?? "Speichern fehlgeschlagen.", ton: "danger" });
        }
    };

    const toggle = async () => {
        const wirdErledigt = !done;
        try {
            await vorgangService.updateTodoStatus(todo.id, toggledTodoStatus(todo.status));
            await handlers.onGeaendert();
        } catch (err: any) {
            console.error("[TodoKarteV4] Status umschalten fehlgeschlagen", err);
            void hinweisen({ titel: "Status nicht geändert", text: err?.message ?? "Der Status konnte nicht geändert werden.", ton: "danger" });
            return;
        }
        // „Und wenn ich erledige, dann muss ich was kurz dazu schreiben?“ —
        // „Ja genau“ (PO 13.09.2026). Derselbe Dialog wie beim Abschluss des
        // VORGANGS, nur mit der Sache im Titel, die hier erledigt wurde.
        //
        // Der Text landet als NOTIZ im Strang des To-dos, nicht in einem
        // eigenen Feld: `zusammenfassung` gibt es nur am Vorgang (Migration
        // 202609090003), und dieses Paket legt keine Migration an. Als Notiz
        // steht er ohnehin an der richtigen Stelle — chronologisch am Ende
        // der Arbeit, die er zusammenfasst.
        //
        // OFFEN für den PO: War es das LETZTE offene To-do, wird damit auch
        // der Vorgang erledigt, und die Seite fragt zusätzlich nach seiner
        // Zusammenfassung — dann stehen zwei Fragen hintereinander (erst die
        // des To-dos, sie liegt oben). Beide sind überspringbar; ob das eine
        // Frage zu viel ist, entscheidet die Abstimmung.
        if (wirdErledigt && canEdit) setAbschlussFrage(true);
    };

    const abschlussNotiz = async (text: string) => {
        if (!text.trim()) return;
        await handlers.onNotizNeu({ text, mentions: [], zuInformieren: [] }, todo.id, "text");
    };

    const entfernen = async () => {
        const ok = await bestaetigen({
            titel: "To-do entfernen?",
            // Bleibt danach genau eines, faltet der Server es in den Vorgang
            // zurück (User 03.11.2026) — das soll vorher dastehen.
            text: entfernenText(todo.titel, kandidaten.length),
            ja: "Entfernen",
            ton: "danger",
        });
        if (!ok) return;
        try {
            await vorgangService.deleteTodo(todo.id);
            await handlers.onGeaendert();
        } catch (err: any) {
            console.error("[TodoKarteV4] Entfernen fehlgeschlagen", err);
            void hinweisen({ titel: "Nicht entfernt", text: err?.message ?? "Entfernen fehlgeschlagen.", ton: "danger" });
        }
    };

    /**
     * Aus dem To-do wird ein eigener Vorgang (PO 22.09.2026).
     *
     * Ohne Rückfrage: Es geht nichts verloren. Seit 03.11.2026 entsteht der
     * neue Vorgang OHNE To-do — das To-do wird der Vorgang (Rückfaltung im
     * Server). Wer sich vertut, verschiebt den Vorgang „als To-do" zurück. Danach gehen wir MIT: Der neue Vorgang ist ab jetzt der Ort,
     * an dem diese Arbeit liegt, und ihn erst suchen zu müssen wäre die
     * eigentliche Zumutung.
     */
    const alsEigenerVorgang = async () => {
        try {
            const { vorgangId: neu } = await vorgangService.todoAlsEigenenVorgang(todo.id);
            await handlers.onGeaendert();
            navigate(`/vorgaenge/${neu}`);
        } catch (err: any) {
            console.error("[TodoKarteV4] Als eigener Vorgang fehlgeschlagen", err);
            void hinweisen({
                titel: "Kein eigener Vorgang",
                text: err?.message ?? "Der eigene Vorgang konnte nicht angelegt werden.",
                ton: "danger",
            });
        }
    };

    const betreffSpeichern = async () => {
        const t = titel.trim();
        if (!t) return;
        if (t !== todo.titel) await patch({ titel: t });
        setUmbenennen(false);
    };

    const rolle = (gruppe: BeteiligtenFeld, label: string) => (
        <BeteiligteFeld
            users={users}
            canEdit={canEdit}
            gruppen={[gruppe]}
            werte={werte}
            onChange={(g, next) => void patchBeteiligte(g, next)}
            popoverTitel={label}
            leerLabel={label}
            mitLegende
            sucheBeiLeerOffen
            // 29 px wie die K·R·V-Kreise in der Zeile darüber (Mockup `.kopf`).
            kreisGroesse={29}
        />
    );


    /** Das ⋯ der Karte — am Handy zusätzlich mit „Umbenennen…" (dort ist der Betreff kein Feld). */
    const mehrMenue = (mitUmbenennen = false) =>
        canEdit && canRemoveTodo(canEdit) ? (
            <RdPopover
                width={205}
                dialogLabel="Weitere To-do-Aktionen"
                align="end"
                trigger={({ toggle: auf, ref }) => (
                    <button
                        ref={ref}
                        type="button"
                        className="vg3-ibtn"
                        title="Weitere To-do-Aktionen"
                        aria-label="Weitere To-do-Aktionen"
                        onClick={auf}
                    >
                        <IcoMehrWaagerecht size={16} />
                    </button>
                )}
            >
                {(close) => (
                    <div className="kvp-rd vg3-menu">
                        {mitUmbenennen && (
                            <button
                                type="button"
                                className="vg3-menu-item"
                                onClick={() => {
                                    close();
                                    setUmbenennen(true);
                                }}
                            >
                                Umbenennen…
                            </button>
                        )}
                        {/* ÜBER dem Entfernen (PO 16.09.2026): der
                            rettende Punkt steht vor dem
                            verwerfenden. Nur mit einem zweiten
                            To-do — ein To-do mit sich selbst
                            zusammenzuführen ergibt nichts. */}
                        {kandidaten.length > 0 && (
                            <button
                                type="button"
                                className="vg3-menu-item"
                                onClick={() => {
                                    close();
                                    setZusammenfuehren(true);
                                }}
                            >
                                To-do zusammenführen…
                            </button>
                        )}
                        {/* Die beiden Wege HINAUS (PO 22.09.2026:
                            „bei einem To-do muss ich dann aber auch
                            bei ⋯ ein To-do zum Vorgang verschieben
                            können / als eigenen Vorgang erstellen
                            können"). Bis dahin ging ein To-do nur
                            „von hinten": Man musste in den
                            ZIEL-Vorgang gehen und es holen.

                            Beide stehen VOR dem Entfernen — der
                            rettende Punkt vor dem verwerfenden,
                            wie beim Zusammenführen. */}
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                setHinaus(true);
                            }}
                        >
                            To-do verschieben…
                        </button>
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                void alsEigenerVorgang();
                            }}
                        >
                            Als eigenen Vorgang
                        </button>
                        <button
                            type="button"
                            className="vg3-menu-item gefahr"
                            onClick={() => {
                                close();
                                void entfernen();
                            }}
                        >
                            To-do entfernen
                        </button>
                    </div>
                )}
            </RdPopover>
        ) : null;

    const strang = offen ? (
        <ArbeitsstrangV4
            {...handlers}
            {...(entwuerfe !== undefined ? { entwuerfe } : {})}
            eintraege={eintraege}
            alleNotizen={alleNotizen}
            users={users}
            canEdit={canEdit}
            aktuellerUserId={aktuellerUserId}
            vorgangId={vorgangId}
            todoId={todo.id}
            contactId={contactId}
            contactName={contactName}
            vorgangNbr={vorgangNbr}
            vertragNr={vertragNr}
            risikoName={risikoName}
            zugewieseneMessageIds={zugewieseneMessageIds}
            vorhandeneDokumentIds={vorhandeneDokumentIds}
            risiken={risiken}
            vertraege={vertraege}
            ohneAktionsleiste={mobil && onHinzufuegen != null}
            kompakt={mobil}
        />
    ) : null;

    const fristenUndRollen = (
        <div className="vg4-z2">
            <div className="vg4-fristen">
                <span className="vg4-frist">
                    <span className="vg4-frist-label">TWV</span>
                    <DatumChipV4
                        label="Wiedervorlage des To-dos"
                        value={todo.twv}
                        canEdit={canEdit}
                        erledigt={done}
                        onSave={(v) => void patch(twvPatch(v, todo.frist) as Partial<TodoDto>)}
                    />
                </span>
                <span className="vg4-frist">
                    <span className="vg4-frist-label">Frist</span>
                    <DatumChipV4
                        label="Frist des To-dos"
                        value={todo.frist}
                        canEdit={canEdit}
                        erledigt={done}
                        onSave={(v) => void patch({ frist: v } as Partial<TodoDto>)}
                    />
                </span>
            </div>

            <div className="vg4-rollen">
                {rolle("hauptverantwortlich", "Hauptverantwortlich")}
                {rolle("bearbeiter", "Bearbeiter")}
                {rolle("zuInformierende", "zu informieren")}
            </div>
        </div>
    );

    const dialoge = (
        <>
            {zusammenfuehren && kandidaten.length > 0 && (
                <TodoZusammenfuehrenDialog
                    quelle={todo}
                    kandidaten={kandidaten}
                    users={users}
                    onZusammengefuehrt={handlers.onGeaendert}
                    onSchliessen={() => setZusammenfuehren(false)}
                />
            )}

            {hinaus && (
                <TodoHinausDialog
                    todo={todo}
                    contactId={contactId ?? null}
                    aktuellerVorgangId={vorgangId}
                    onVerschoben={handlers.onGeaendert}
                    onSchliessen={() => setHinaus(false)}
                />
            )}

            {abschlussFrage && (
                <ZusammenfassungDialog
                    betreff={todo.titel || `To-do ${todo.id}`}
                    wert=""
                    titel="To-do erledigt — was kam heraus?"
                    onSpeichern={(text) => abschlussNotiz(text)}
                    onSchliessen={() => setAbschlussFrage(false)}
                />
            )}
        </>
    );

    /*
     * DIE KARTE AUF DEM HANDY (Artifact „Vorgangsdetail mobil", Artboard 1):
     *
     *     (○)  To-do-Betreff                       ∨
     *          [Heute fällig]  2 Notizen · 1 im Verlauf
     *
     * Der Haken ist 44 px breit; die ganze übrige Zeile klappt auf. Aufgeklappt
     * stehen K·R·V, das A und ⋯ (mit „Umbenennen…"), darunter Fristen und
     * Rollen, dann Notizen und Verlauf. Ziehen und Dateiablage gibt es hier
     * nicht — auf Touch löst beides nichts aus, die Wege stehen im ⋯.
     */
    if (mobil) {
        const pille = fristPille({ frist: todo.frist, twv: todo.twv, erledigt: done });
        return (
            <section
                className={`vg4-card vg4-todo vg4m-todo${offen ? " open" : ""}${done ? " done" : ""}`}
                data-todo-karte={todo.id}
            >
                <div className="vg4m-todo-zeile">
                    <button
                        type="button"
                        className={`vg4m-haken${done ? " done" : ""}`}
                        disabled={!canEdit}
                        aria-pressed={done}
                        aria-label={`${done ? "Wieder öffnen" : "Als erledigt markieren"}: ${todo.titel || "To-do"}`}
                        title={canEdit ? undefined : "Nur Berechtigte können den Status ändern"}
                        onClick={() => void toggle()}
                    >
                        <span className="vg4m-haken-kreis" aria-hidden>
                            {done && <IcoCheck size={13} />}
                        </span>
                    </button>
                    {/* Titel und Chevron klappen auf; die Frist-Pille dazwischen
                        ist ein eigener Knopf (PR 7) — sie öffnet die Fristen. */}
                    <div className="vg4m-todo-mitte">
                        <button
                            type="button"
                            className="vg4m-todo-kopf"
                            aria-expanded={offen}
                            aria-label={`${todo.titel || "(ohne Betreff)"} — ${offen ? "zuklappen" : "aufklappen"}`}
                            onClick={() => setOffen((o) => !o)}
                        >
                            <span className="vg4m-todo-titel">{todo.titel || "(ohne Betreff)"}</span>
                        </button>
                        <span className="vg4m-todo-meta">
                            {pille || (canEdit && !done) ? (
                                <button
                                    type="button"
                                    className={`vg4m-frist-pille ${pille?.ton ?? "leer"}`}
                                    disabled={!canEdit}
                                    aria-label={`Fristen von „${todo.titel || "To-do"}“: ${pille?.text ?? "keine"}`}
                                    onClick={() => setFristSheet(true)}
                                >
                                    {pille?.text ?? "+ Frist"}
                                </button>
                            ) : null}
                            <span>{zaehlung || "noch nichts"}</span>
                        </span>
                    </div>
                    <button
                        type="button"
                        className={`vg4m-chev${offen ? " open" : ""}`}
                        aria-hidden
                        tabIndex={-1}
                        onClick={() => setOffen((o) => !o)}
                    >
                        <IcoChevronUnten size={18} />
                    </button>
                </div>
                <FristSheet
                    offen={fristSheet}
                    titel={todo.titel || `To-do ${todo.id}`}
                    werte={{ twv: todo.twv ?? null, frist: todo.frist ?? null }}
                    onSchliessen={() => setFristSheet(false)}
                    onSpeichern={(p) => patch(p as Partial<TodoDto>)}
                />
                {offen && (
                    <div className="vg4m-todo-inhalt">
                        {umbenennen && (
                            <input
                                autoFocus
                                className="vg4-betreff vg4m-todo-umbenennen"
                                value={titel}
                                aria-label="Betreff des To-dos"
                                onChange={(e) => setTitel(e.target.value)}
                                onBlur={() => void betreffSpeichern()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") void betreffSpeichern();
                                    if (e.key === "Escape") {
                                        setTitel(todo.titel);
                                        setUmbenennen(false);
                                    }
                                }}
                            />
                        )}
                        <div className="vg4m-todo-werkzeug">
                            <BezugsKreise
                                slots={slotsFromDto(todo)}
                                canEdit={canEdit}
                                onApply={(r: CascadeResult) => void patch(r.patch as Partial<TodoDto>)}
                            />
                            <AustauschKnopfV4
                                vorgangId={vorgangId}
                                todoId={todo.id}
                                austausch={austausch ?? []}
                                users={users}
                                canEdit={canEdit}
                                onGeaendert={onAustauschGeaendert ?? handlers.onGeaendert}
                                alsSheet
                            />
                            <span className="vg4m-todo-werkzeug-r">{mehrMenue(true)}</span>
                        </div>
                        {fristenUndRollen}
                        {strang}
                        {canEdit && onHinzufuegen && (
                            <button type="button" className="vg4m-hinzufuegen" onClick={onHinzufuegen}>
                                <IcoPlus size={15} /> Hinzufügen
                            </button>
                        )}
                    </div>
                )}
                {dialoge}
            </section>
        );
    }

    return (
        <DateiDropZoneV4
            className={`vg4-card vg4-todo${offen ? " open" : ""}${done ? " done" : ""}${treffer ? " vg4-todo-treffer" : ""}`}
            vorgangId={vorgangId}
            todoId={todo.id}
            zielName={todo.titel || `To-do ${todo.id}`}
            canEdit={canEdit}
            onGeaendert={handlers.onGeaendert}
        >
            {/* ZIEHEN UND ABLEGEN (PO 28.09.2026). `display: contents` — die
                Hülle fängt nur die Ereignisse, die aus der Karte aufsteigen,
                und verändert das Layout nicht. Dateien bleiben Sache von
                `DateiDropZoneV4`: Die Handler hier reagieren ausschließlich
                auf den To-do-Typ. */}
            <div
                style={{ display: "contents" }}
                data-todo-karte={todo.id}
                onDragEnter={(e) => {
                    if (!nimmtZugAn(e)) return;
                    e.preventDefault();
                    trefferTiefe.current += 1;
                    setTreffer(true);
                }}
                onDragOver={(e) => {
                    if (!nimmtZugAn(e)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                }}
                onDragLeave={(e) => {
                    if (!nimmtZugAn(e)) return;
                    trefferTiefe.current = Math.max(0, trefferTiefe.current - 1);
                    if (trefferTiefe.current === 0) setTreffer(false);
                }}
                onDrop={(e) => {
                    if (!nimmtZugAn(e)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    trefferTiefe.current = 0;
                    setTreffer(false);
                    try {
                        const zug = JSON.parse(e.dataTransfer.getData(ZIEHTYP_TODO)) as TodoZug;
                        if (darfAufTodo(zug, { id: todo.id, vorgangId })) setZugQuelleId(zug.id);
                    } catch {
                        // Fremder Zug mit unserem Typ — nichts tun.
                    }
                }}
            >
            {/* Betreffzeile VOR den Fristen (PO 13.09.2026) — identisch zum
                Vorgangskopf: erst worum es geht, dann bis wann.
                Sie ist zugleich der GRIFF zum Ziehen (PO 28.09.2026) — nicht
                während des Umbenennens, sonst ließe sich im Feld kein Text
                markieren. */}
            <div
                className="vg4-z3"
                draggable={canEdit && !umbenennen}
                title={canEdit && !umbenennen ? "Auf ein anderes To-do ziehen, um beide zusammenzuführen" : undefined}
                onDragStart={(e) => {
                    const zug: TodoZug = { id: todo.id, titel: todo.titel ?? "", vorgangId };
                    e.dataTransfer.setData(ZIEHTYP_TODO, JSON.stringify(zug));
                    e.dataTransfer.effectAllowed = "move";
                    gezogeneKarte = { id: todo.id, vorgangId };
                }}
                onDragEnd={() => {
                    gezogeneKarte = null;
                }}
            >
                {/* Der sichtbare GRIFF (T-15): Bis dahin war die ganze Zeile
                    ziehbar, man sah es ihr aber nicht an — und wer den Betreff
                    „anfasste", landete im Umbenennen-Feld. Kein eigener
                    Handler: Der Zug startet über das `onDragStart` der Zeile,
                    und ein Klick hier löst weder Umbenennen noch Ausklappen
                    aus. Beim Umbenennen nur ausgeblendet, damit der Betreff
                    nicht springt. */}
                {canEdit && (
                    <span
                        className={`vg4-z3-griff${umbenennen ? " aus" : ""}`}
                        aria-hidden="true"
                        title={umbenennen ? undefined : "Auf ein anderes To-do ziehen, um beide zusammenzuführen"}
                    >
                        <svg width="10" height="16" viewBox="0 0 10 16" focusable="false">
                            {[3, 8, 13].map((y) => (
                                <React.Fragment key={y}>
                                    <circle cx="2.5" cy={y} r="1.4" />
                                    <circle cx="7.5" cy={y} r="1.4" />
                                </React.Fragment>
                            ))}
                        </svg>
                    </span>
                )}
                {/* Das Ausklappen steht LINKS vor dem Betreff (PO 13.09.2026):
                    „Du arbeitest ja eher links und rechts ist ja eher
                    Ansichtnahme.“ Vorher saß es am rechten Ende der Zeile,
                    hinter fünf anderen Bedienelementen. */}
                <button
                    type="button"
                    className={`vg4-chev${offen ? " open" : ""}`}
                    aria-expanded={offen}
                    aria-label={offen ? "To-do zuklappen" : "To-do aufklappen"}
                    title={offen ? "To-do zuklappen" : "To-do aufklappen"}
                    onClick={() => setOffen((o) => !o)}
                >
                    <IcoChevronUnten size={15} />
                </button>

                {umbenennen ? (
                    <input
                        autoFocus
                        className="vg4-betreff"
                        value={titel}
                        aria-label="Betreff des To-dos"
                        onChange={(e) => setTitel(e.target.value)}
                        onBlur={() => void betreffSpeichern()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") void betreffSpeichern();
                            if (e.key === "Escape") {
                                setTitel(todo.titel);
                                setUmbenennen(false);
                            }
                        }}
                    />
                ) : (
                    <button
                        type="button"
                        className="vg4-betreff vg4-betreff-btn"
                        disabled={!canEdit}
                        onClick={() => {
                            setUmbenennen(true);
                            setOffen(true);
                        }}
                    >
                        {todo.titel || "(ohne Betreff)"}
                    </button>
                )}

                {/* Die Reihe rechts steht seit der Zeichnung vom 13.09.2026 in
                    dieser Ordnung: [K R V] [⊕] [Anzahl] [⋯] [als erledigt
                    markieren]. Erst die Bezüge, dann das Hinzufügen, dann die
                    Auskunft — und ganz rechts der Abschluss, „das ist ja echt
                    das Finale“. Seit T-302 (PO 29.10.2026) steht an der
                    Stelle des ⊕ das A des Austauschs. */}
                <div className="vg4-z3-r">
                    <BezugsKreise
                        slots={slotsFromDto(todo)}
                        canEdit={canEdit}
                        onApply={(r: CascadeResult) => void patch(r.patch as Partial<TodoDto>)}
                    />

                    {/* DAS A STATT DES ⊕ (T-302). Was das ⊕ hier anbot, bleibt:
                        Risiko/Vertrag an den Kreisen links daneben, Mail,
                        Anruf, Dokument und Termin in der Aktionsleiste des
                        aufgeklappten To-dos (`ArbeitsstrangV4`). Der
                        Austausch gehört zu DIESEM To-do. */}
                    <AustauschKnopfV4
                        vorgangId={vorgangId}
                        todoId={todo.id}
                        austausch={austausch ?? []}
                        users={users}
                        canEdit={canEdit}
                        onGeaendert={onAustauschGeaendert ?? handlers.onGeaendert}
                    />

                    {/* Der graue Zähler stand hier bis zum 13.09.2026 („6
                        Einträge · erledigt“). PO: raus. Den Status trägt
                        weiterhin ohne Farbe, wer ihn braucht — der Betreff ist
                        durchgestrichen, und der Knopf rechts sagt „erledigt“
                        im Klartext. */}
                    {mehrMenue()}

                    {/* Erledigen steht auch ZUGEKLAPPT in der Reihe
                        (Zeichnung 13.09.2026).
                        ANNAHME — in genau diesem einen Block umkehrbar: bis
                        zum 09.09.2026 galt die Gegenregel „Erledigen erst
                        AUFGEKLAPPT“, weil in einer Liste zugeklappter To-dos
                        „als erledigt markieren“ sonst der breiteste Text der
                        Zeile war, neben einem Betreff, der die Überschrift
                        sein soll. Die neuere Vorgabe gilt; die alte Begründung
                        bleibt hier nachlesbar. RÜCKFALLWEG, falls der PO die
                        Reihe zugeklappt zu voll findet: zugeklappt nur das
                        Häkchen-Icon zeigen, aufgeklappt Icon + Text — also
                        `{offen && (done ? "erledigt" : "als erledigt
                        markieren")}` statt des festen Textes. */}
                    <button
                        type="button"
                        className={`vg3-erledigt${done ? " done" : ""}`}
                        disabled={!canEdit}
                        aria-pressed={done}
                        title={canEdit ? undefined : "Nur Berechtigte können den Status ändern"}
                        onClick={() => void toggle()}
                    >
                        <span className={`vg3-check${done ? " done" : ""}`} aria-hidden>
                            <IcoCheck size={11} />
                        </span>
                        {done ? "erledigt" : "als erledigt markieren"}
                    </button>
                </div>
            </div>

            {fristenUndRollen}

            {/* Hier stand bis zum 21.09.2026 „1 Notiz · 3 im Verlauf" unter der
                zugeklappten Karte (PO 15.09.2026). Ersatzlos raus (PO
                21.09.2026): Die Zahl beantwortete keine Frage, die jemand
                hatte — wer wissen will, was drinsteht, klappt auf. Die
                Zählung selbst lebt weiter, sie trägt die Kandidatenliste in
                „To-do zusammenführen…" (`strangZaehlung.ts`). */}
            {strang}

            {/* Fragt NACH, hält nichts auf: das Erledigen ist bereits
                gespeichert, wenn dieser Dialog erscheint — „Später“ ist
                erlaubt. Ein Pflichtfeld an dieser Stelle machte aus dem
                häufigsten Handgriff eine Mautstelle. */}
            {dialoge}

            {/* Ein Geschwister ist auf diese Karte gefallen: derselbe Dialog
                wie über ⋯, mit DIESEM To-do als Ziel. */}
            {zugQuelle && zugKandidaten.length > 0 && (
                <TodoZusammenfuehrenDialog
                    quelle={zugQuelle}
                    kandidaten={zugKandidaten}
                    vorgewaehltesZielId={todo.id}
                    betreffVon="quelle"
                    users={users}
                    onZusammengefuehrt={handlers.onGeaendert}
                    onSchliessen={() => setZugQuelleId(null)}
                />
            )}
            </div>
        </DateiDropZoneV4>
    );
};
